import { useMemo, useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Bot, ArrowRight } from 'lucide-react';
import { tokens } from '../theme';
import { Icon } from '../icons';
import { GIROS, MODULES, EXTERNAL_URLS } from '../data';

/**
 * GirosDiagram — Fase 3. El visitante elige su giro (30 opciones, incluidas las especialidades de
 * LeadCEM) y ve un DIAGRAMA interactivo: un núcleo de IA con los MÓDULOS REALES que se "encienden"
 * para ese giro (radial + líneas de circuito animadas), más la lista legible con nombres reales.
 * Cableado a MODULES (keys de plan_access), no inventado. Temizado día/noche; respeta reduce-motion.
 */
const MOD_BY_KEY = Object.fromEntries(MODULES.map((m) => [m.key, m]));

export default function GirosDiagram() {
  const { theme } = useTheme();
  const t = tokens(theme);
  const [sel, setSel] = useState(GIROS[0]);

  const mods = useMemo(
    () => (sel.modules || []).map((k) => MOD_BY_KEY[k]).filter(Boolean),
    [sel]
  );

  // posiciones radiales en un viewBox 100×100 (centro 50,50, radio 41)
  const nodes = useMemo(() => {
    const N = mods.length || 1;
    return mods.map((m, i) => {
      const ang = (-90 + (360 / N) * i) * (Math.PI / 180);
      return { m, x: 50 + 41 * Math.cos(ang), y: 50 + 41 * Math.sin(ang) };
    });
  }, [mods]);

  return (
    <div data-testid="giros-diagram">
      <style>{`
        @keyframes lcnode { 0% { opacity:0; transform:translate(-50%,-50%) scale(.4); } 100% { opacity:1; transform:translate(-50%,-50%) scale(1); } }
        @keyframes lcline { from { stroke-dashoffset:42; } to { stroke-dashoffset:0; } }
        @keyframes lccore { 0%,100% { transform:scale(1); opacity:.5; } 50% { transform:scale(1.4); opacity:0; } }
        @keyframes lcscan { to { transform: rotate(360deg); } }
        @keyframes lcglow { 0%,100% { box-shadow:0 0 22px rgba(124,194,62,.4); } 50% { box-shadow:0 0 40px rgba(124,194,62,.75); } }
        .lc-node { animation: lcnode .5s cubic-bezier(.2,.8,.2,1) both; }
        .lc-line { stroke-dasharray:42; animation: lcline .6s ease-out both; }
        .lc-corepulse { animation: lccore 2.4s ease-out infinite; }
        .lc-scan { background: conic-gradient(from 0deg, rgba(124,194,62,0) 0deg, rgba(124,194,62,0) 268deg, rgba(139,206,67,.5) 335deg, rgba(214,255,170,.9) 356deg, rgba(124,194,62,0) 360deg); animation: lcscan 1.9s linear infinite; }
        .lc-coreglow { animation: lcglow 2.4s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .lc-node, .lc-line, .lc-corepulse, .lc-scan, .lc-coreglow { animation:none !important; stroke-dashoffset:0 !important; }
          .lc-node { opacity:1 !important; }
          .lc-scan { opacity:.25; }
        }
      `}</style>

      {/* Selector de giros */}
      <div className="flex flex-wrap gap-2 justify-center max-w-4xl mx-auto mb-10" role="tablist" aria-label="Elige tu giro">
        {GIROS.map((g) => {
          const active = g.key === sel.key;
          return (
            <button
              key={g.key}
              role="tab"
              aria-selected={active}
              onClick={() => setSel(g)}
              className={`text-sm rounded-full px-3.5 py-2 border transition-all ${
                active
                  ? 'bg-lime-400 text-slate-950 border-lime-400 font-semibold shadow-md shadow-lime-400/30'
                  : `${t.surface} ${t.textSub} hover:border-lime-400/50 ${g.featured ? (t.dark ? 'ring-1 ring-lime-400/30' : 'ring-1 ring-lime-500/30') : ''}`
              }`}
              data-testid={`giro-chip-${g.key}`}
              title={g.featured ? 'Especialidad LeadCEM' : undefined}
            >
              <span aria-hidden="true">{g.emoji}</span> {g.label}
              {g.featured && <span className={`ml-1 ${active ? 'text-slate-900' : t.accentText}`} aria-hidden="true">★</span>}
            </button>
          );
        })}
      </div>

      {/* Diagrama + detalle */}
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-center max-w-5xl mx-auto">
        {/* Radial (se remonta al cambiar de giro → re-anima) */}
        <div className="relative w-full max-w-[420px] mx-auto aspect-square" key={sel.key}>
          <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" aria-hidden="true">
            <circle cx="50" cy="50" r="41" fill="none" stroke={t.dark ? '#ffffff' : '#1c3f74'} strokeOpacity="0.08" strokeWidth="0.3" />
            {nodes.map((n, i) => (
              <line
                key={i}
                x1="50" y1="50" x2={n.x} y2={n.y}
                stroke="#7cc23e" strokeWidth="0.5" strokeOpacity={t.dark ? 0.35 : 0.5}
                className="lc-line" style={{ animationDelay: `${i * 55}ms` }}
              />
            ))}
          </svg>

          {nodes.map((n, i) => (
            <div
              key={i}
              className="lc-node absolute"
              style={{ left: `${n.x}%`, top: `${n.y}%`, transform: 'translate(-50%,-50%)', animationDelay: `${i * 55}ms` }}
              title={n.m.name}
            >
              <div className={`w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center ${t.iconChip} ${n.m.only_leadcem ? 'ring-2 ring-lime-400/60' : ''}`}>
                <Icon name={n.m.icon} className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
            </div>
          ))}

          {/* núcleo IA */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
            <div className="relative">
              {/* escaneo radar alrededor del núcleo */}
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-36 h-36 sm:w-48 sm:h-48" aria-hidden="true">
                <span className="lc-scan block w-full h-full rounded-full" />
              </span>
              <span className="lc-corepulse absolute inset-0 rounded-full bg-lime-400" aria-hidden="true" />
              <div className="lc-coreglow relative w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#1c3f74] border-2 border-lime-400/80 flex flex-col items-center justify-center text-white">
                <Bot className="w-6 h-6 sm:w-7 sm:h-7 text-lime-300" />
                <span className="text-[9px] font-bold tracking-wide mt-0.5">IA</span>
              </div>
            </div>
          </div>
        </div>

        {/* Detalle del giro seleccionado */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-3xl" aria-hidden="true">{sel.emoji}</span>
            <div>
              <h3 className={`text-xl sm:text-2xl font-semibold ${t.text}`} data-testid="giro-detail-title">{sel.label}</h3>
              {sel.featured && <span className={`text-[11px] font-bold uppercase tracking-wide ${t.accentText}`}>★ Especialidad LeadCEM</span>}
            </div>
          </div>
          <p className={`text-sm sm:text-base ${t.textSub} leading-relaxed`} data-testid="giro-detail-pitch">{sel.pitch}</p>
          <p className={`text-xs font-semibold uppercase tracking-wide ${t.textFaint}`}>
            {mods.length} módulos se encienden para este giro
          </p>
          <div className="flex flex-wrap gap-1.5">
            {mods.map((m) => (
              <span key={m.key} className={`inline-flex items-center gap-1.5 text-xs rounded-lg px-2 py-1 ${t.surface} ${t.textSub}`}>
                <Icon name={m.icon} className={`w-3.5 h-3.5 ${t.accentText}`} />
                {m.name}
                {m.only_leadcem && <span className={`text-[9px] font-bold ${t.accentText}`}>· solo LeadCEM</span>}
              </span>
            ))}
          </div>
          <a href={EXTERNAL_URLS.whatsapp} target="_blank" rel="noopener noreferrer" className="inline-block pt-2">
            <Button className="lc-shine bg-lime-400 hover:bg-lime-300 text-slate-950 rounded-full h-11 px-6 font-semibold" data-testid="giro-cta">
              Quiero esto para mi negocio <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
}
